import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { BsPlayCircleFill } from 'react-icons/bs';

import { Loading } from '../BayPolotnoEditor/Loading';
import { createJsonFromData } from '../BayPolotnoEditor/utils';

import './Previewer.scss';

export interface PreviewerProps {
	workspaceComponent: React.ComponentType<any>;
	template: any;
	user: any;
	listing?: any;
	previewStore: any;
	phoneFormatValue?: string;
	altos?: any;
}

export const Previewer: React.FC<PreviewerProps> = observer((props) => {
	const Workspace = props.workspaceComponent;
	const [isLoading, setIsLoading] = React.useState(true);

	// 1. Setup state and ref for the video player
	const [isPlaying, setIsPlaying] = useState(false);
	const videoRef = useRef<HTMLVideoElement>(null);

	// Extract the video URL from props
	const videoUrl = props.template?.etc?.previewVideo;

	useEffect(() => {
		setIsLoading(true);

		const now = dayjs();
		const {
			logoLight,
			logoDark,
			primaryColor,
			secondaryColor,
			accentColor,
			tertiaryColor,
		} = props.user?.brand ?? {};
		// TODO: handle when the logo is an SVG url
		const json = createJsonFromData(
			props.template?.json ?? props.template?.source?.data,
			props.template?.directives ?? props.template?.etc?.directives,
			{
				listing: props.listing,
				user: {
					...props.user,
					...(props.user?.profileImages?.length
						? { imageUrl: props.user.profileImages[0] }
						: {}),
					...(props.user?.brand
						? {
								logoLightUrl: logoLight,
								logoDarkUrl: logoDark,
								brandPrimaryColor: primaryColor,
								brandSecondaryColor: secondaryColor,
								brandAccentColor: accentColor,
								brandTertiaryColor: tertiaryColor,
						  }
						: {}),
				},
				disclaimer: props.user?.disclaimer,
				preferences: {
					phoneFormat: props.phoneFormatValue,
				},
				current_date: {
					year: now.format('YYYY'),
					month: now.format('MMMM'),
					day: now.format('D'),
				},
				// Always present so a bare {{content_locations.*}} tag falls to its layer default, never leaks raw
				content_locations: props.user?.addresses?.[0]
					? {
							city: props.user.addresses[0].city,
							zip: props.user.addresses[0].postalCode,
					  }
					: {},
				altos: props.altos,
			},
			undefined,
			true,
		);

		props.previewStore.loadJSON(json);

		props.previewStore.waitLoading().then(() => {
			setIsLoading(false);
		});
	}, []);

	const handlePlayClick = () => {
		setIsPlaying(true);
		if (videoRef.current) {
			videoRef.current.play();
		}
	};

	return (
		<Container
			className="d-flex align-items-center justify-content-center"
			style={{ height: '75vh' }}
		>
			<div className="position-relative w-100 h-100">
				{isLoading ? (
					<Loading message="Loading Preview" />
				) : videoUrl ? (
					<div className="position-relative w-100 h-100">
						<video
							ref={videoRef}
							src={videoUrl}
							className="w-100 h-100"
							controls={isPlaying}
							poster={
								props.template?.rasters?.find(
									(e: any) => e.formatKey === 'thumbnail',
								)?.url
							}
						/>
						{!isPlaying && (
							<div
								onClick={handlePlayClick}
								className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center previewer-video-overlay"
							>
								<BsPlayCircleFill
									size={70}
									color="white"
									className="previewer-video-play-icon"
								/>
							</div>
						)}
					</div>
				) : (
					<Workspace
						backgroundColor="white"
						bleedColor="rgba(255, 0, 0, 0.2)"
						components={{ PageControls: () => null }}
						pageBorderColor="rgba(0, 0, 0, 0.175)"
						store={props.previewStore}
					/>
				)}
			</div>
		</Container>
	);
});

export default Previewer;
